<template>
	<div class="modal-card reload_fintoc_card">
		<header class="modal-card-head rf-head">
			<div class="rf-head__intro">
				<p class="rf-eyebrow">Fintoc</p>
				<h3 class="rf-title">Recargar {{ tipo }}</h3>
				<p class="rf-sub">Empresa {{ rutCompany }}</p>
			</div>
			<button class="rf-close" aria-label="Cerrar Modal" @click="$emit('close')">×</button>
		</header>
		<section class="modal-card-body rf-body">
			<div class="rf-section">
				<p class="rf-label">Período a recargar</p>
				<div class="rf-dates">
					<div class="rf-field">
						<label class="rf-field__label" for="desdeDate">Desde</label>
						<input id="desdeDate" class="fecha_input" type="date" name="desdeDate"
							v-model="sinceDate" :max="untilDate || today">
					</div>
					<div class="rf-field">
						<label class="rf-field__label" for="hastaDate">Hasta</label>
						<input id="hastaDate" class="fecha_input" type="date" name="hastaDate"
							v-model="untilDate" :max="today">
					</div>
				</div>
				<p v-if="rangeError" class="rf-range-error">
					El rango es inválido: "Desde" debe ser anterior o igual a "Hasta".
				</p>
			</div>

			<div class="rf-section">
				<div class="rf-logs-head">
					<p class="rf-label">Recepciones</p>
					<!-- Latido del polling (cada 5 s tras recargar) — reemplaza
						el spinner.gif servido por hostname hardcodeado. -->
					<span v-if="intervalId" class="rf-live">
						<span class="rf-live__dot" aria-hidden="true"></span> actualizando
					</span>
				</div>
				<div class="rf-table-wrap">
					<table class="fintoc_log">
						<tr class="log_row log_header">
							<td class="log_cell">Periodo</td>
							<td class="log_cell">Fecha recepción</td>
							<td class="log_cell is-num">Filas recibidas</td>
							<td class="log_cell is-num">Filas grabadas</td>
							<td class="log_cell">Error</td>
						</tr>
						<tr v-if="!fintoc_logs.length" class="log_row">
							<td class="log_cell rf-empty" colspan="5">
								Sin recepciones registradas para esta empresa.
							</td>
						</tr>
						<tr v-for="row in fintoc_logs" :key="row.id" class="log_row">
							<td class="log_cell">{{ row.tax_period }}</td>
							<td :class="['log_cell', 'log_fecha', { 'occurred_today': row.occurred_today }]">{{ row.received_date }}</td>
							<td class="log_cell is-num">{{ row.rows_received }}</td>
							<td class="log_cell is-num" :class="{ 'mismatch': row.rows_saved != row.rows_received }">{{ row.rows_saved }}</td>
							<td class="log_cell error_cell">
								<SimpleButton @click="showError(row)" v-if="row.error_expand">
									{{ row.expanded ? 'Contraer' : 'Ver error' }}
								</SimpleButton>
								{{ row.error_shown }}
							</td>
						</tr>
					</table>
				</div>
			</div>
		</section>
		<footer class="modal-card-foot rf-foot">
			<span class="rf-foot__note">Trae de Fintoc los documentos del período elegido.</span>
			<span class="rf-foot__spacer" aria-hidden="true"></span>
			<button class="button" @click="$emit('close')" aria-label="Cerrar Modal" role="button">
				Volver
			</button>
			<button class="button is-primary"
				:disabled="loading || !validRange" role="button" @click="reloadData()">
				Recargar
			</button>
		</footer>
	</div>
</template>

<script>
import { updateObligationsFintoc, loadLogs } from "@/api/fintoc";
import { consoleHandlerError, notificate } from "@/utils/message";
import SimpleButton from "@/components/modals/SimpleButton";

export default {
	name: "ReloadFintocByCompany",
	components: {
		SimpleButton,
	},
	data() {
		return {
			loading: false,
			// Rango a recargar, bindeado a los <input type="date"> (YYYY-MM-DD).
			// Antes los pickers no tenían v-model y el request salía siempre con
			// un rango fijo de 2 años → todo el histórico, eligieras lo que
			// eligieras.
			sinceDate: '',
			untilDate: '',
			today: '',
			fintoc_logs: [],
			company_id: '',
			intervalId: null,
		};
	},
	computed: {
		tipo: function() { return this.product == 'sii'? "Obligaciones": "Movimientos Bancarios"; },
		validRange: function () {
			// Strings ISO (YYYY-MM-DD): la comparación lexicográfica es cronológica.
			return Boolean(this.sinceDate) && Boolean(this.untilDate) &&
				this.sinceDate <= this.untilDate;
		},
		rangeError: function () {
			// Solo cuando las dos fechas están puestas y el orden es el problema
			// (una fecha vacía ya deshabilita "Recargar" sin regañar).
			return Boolean(this.sinceDate) && Boolean(this.untilDate) &&
				!this.validRange;
		},
	},
	mounted() {
		this.calculateDates();
		this.loadLogs(this.product);
	},
	methods: {
		showError(row) {
			if (!row.expanded) {
				row.expanded = true;
				row.error_shown = row.error_full;
			} else {
				row.expanded = false;
				row.error_shown = row.error_short;
			}
		},
		async loadLogs(product) {
			// Corre en un setInterval cada 5s mientras el modal está abierto:
			// un fallo transitorio no debe convertirse en unhandled rejection.
			try {
				const result = await loadLogs(this.rutCompany, product);
				this.fintoc_logs = result.data;
			} catch (error) {
				consoleHandlerError({ error, empty: true });
			}
		},
		calculateDates() {
			const today = new Date();
			this.today = today.toISOString().split('T')[0];

			const dateTwoYearsAgo = new Date(today.setFullYear(today.getFullYear() - 2));
			this.sinceDate = dateTwoYearsAgo.toISOString().split('T')[0];
			this.untilDate = this.today;
		},
		reloadData() {
			if (!this.validRange) {
				return;
			}
			this.loading = true
			let data = {
				since: this.sinceDate,
				until: this.untilDate,
				company: this.rutCompany,
				product: this.product
			}
			updateObligationsFintoc(data).then(({ data }) => {
				notificate(
					`<strong>Éxito!</strong><br>
					` + data,
					"is-success is-light"
				);
				this.intervalId = setInterval(() => {
					this.loadLogs(this.product);
				}, 5000);
			}).catch(error => {
				consoleHandlerError({ error, empty: true });
				this.$emit("close");
			}).finally(() => {
				this.loading = false
			})
		},
	},
	props: ["rutCompany", "product"],
	beforeDestroy() {
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}
	},
};
</script>
<style scoped lang="scss">
// ============================================================================
// Recargar desde Fintoc — mismo sistema del Panel Analista (var(--ap-*),
// elements/_analyst_theme.scss): secciones con eyebrow separadas por
// hairlines, tabla de recepciones con reglas horizontales, y latido de
// polling en vivo. Los desvíos (filas grabadas ≠ recibidas) van en alerta.
// ============================================================================
.reload_fintoc_card {
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	width: min(720px, 94vw);
	display: flex;
	flex-direction: column;
	max-height: 86vh;
	background-color: var(--ap-paper);
	border-radius: var(--ap-radius);
	overflow: hidden;
}

// Encabezado -----------------------------------------------------------------
.rf-head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
	padding: 22px 28px 18px;
	background-color: var(--ap-paper);
	border-bottom: 1px solid var(--ap-line);
}

.rf-eyebrow {
	margin: 0 0 4px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.14em;
	text-transform: uppercase;
	color: var(--ap-teal);
}

.rf-title {
	margin: 0;
	font-family: var(--ap-font);
	font-weight: 700;
	font-size: 21px;
	letter-spacing: -0.01em;
	color: var(--ap-ink);
}

.rf-sub {
	margin: 4px 0 0;
	font-size: 13px;
	color: var(--ap-slate);
	font-variant-numeric: tabular-nums;
}

.rf-close {
	width: 30px;
	height: 30px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: var(--ap-slate);
	font-size: 20px;
	line-height: 1;
	cursor: pointer;
	transition: background-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease);

	&:hover {
		background-color: var(--ap-mist);
		color: var(--ap-ink);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 2px;
	}
}

// Cuerpo ----------------------------------------------------------------------
.rf-body {
	background-color: var(--ap-paper);
	color: var(--ap-ink-2);
	padding: 6px 28px 22px;
	overflow-y: auto;
}

.rf-section {
	padding: 18px 0;

	& + .rf-section {
		border-top: 1px solid var(--ap-line);
	}
}

.rf-label {
	margin: 0 0 12px;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

// Período ----------------------------------------------------------------------
.rf-dates {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
	gap: 14px 20px;
}

.rf-field__label {
	display: block;
	margin-bottom: 6px;
	font-size: 12.5px;
	font-weight: 600;
	color: var(--ap-slate);
}

.fecha_input {
	width: 100%;
	padding: 8px 11px;
	border: 1px solid var(--ap-line-strong);
	border-radius: var(--ap-radius-sm);
	font-family: var(--ap-font);
	font-size: 13.5px;
	color: var(--ap-ink);
	background-color: var(--ap-paper);
	transition: border-color 0.14s var(--ap-ease), box-shadow 0.14s var(--ap-ease);

	&:focus {
		outline: none;
		border-color: var(--ap-teal);
		box-shadow: 0 0 0 3px var(--ap-teal-wash);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 1px;
	}
}

.rf-range-error {
	margin: 10px 0 0;
	font-size: 12.5px;
	font-weight: 500;
	color: var(--ap-alert);
}

// Recepciones -------------------------------------------------------------------
.rf-logs-head {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
}

.rf-live {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-size: 11.5px;
	font-weight: 600;
	color: var(--ap-teal-deep);
}

.rf-live__dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background-color: var(--ap-teal);
	animation: rf-pulse 1.6s ease-in-out infinite;
}

@keyframes rf-pulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.25; }
}

.rf-table-wrap {
	overflow-x: auto;
}

.fintoc_log {
	width: 100%;
	border-collapse: collapse;
}

.log_cell {
	border: none;
	border-bottom: 1px solid var(--ap-line);
	padding: 9px 10px;
	font-size: 13px;
	color: var(--ap-ink-2);
	text-align: left;

	&.is-num {
		text-align: right;
		font-variant-numeric: tabular-nums;
	}
}

.log_row:not(.log_header) {
	transition: background-color 0.14s var(--ap-ease);

	&:hover {
		background-color: var(--ap-teal-wash);
	}
}

.log_fecha {
	font-size: 12px;
	color: var(--ap-slate);
	font-variant-numeric: tabular-nums;
}

.log_header {
	.log_cell {
		border-bottom: 1px solid var(--ap-line-strong);
		padding: 7px 10px;
		background-color: transparent;
		color: var(--ap-muted);
		font-weight: 600;
		font-size: 10.5px;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		white-space: nowrap;
	}
}

.rf-empty {
	padding: 32px 10px;
	text-align: center;
	color: var(--ap-muted);
	font-size: 13px;
	border-bottom: none;
}

// Discrepancia filas grabadas ≠ recibidas → énfasis de alerta.
.mismatch {
	background-color: var(--ap-alert-wash);
	color: var(--ap-alert);
	font-weight: 600;
}

// Recepción del día → resaltado positivo (recibido recientemente).
.occurred_today {
	background-color: var(--ap-ok-wash);
	color: var(--ap-ok-ink);
	font-weight: 600;
}

// Botón "Ver error" (SimpleButton compartido): lo reestilamos como pill de
// alerta dentro de este modal sin tocar el componente. La clase extra en el
// <td> sube la especificidad por encima de los estilos propios del hijo.
.error_cell :deep(.simple_button){
	border: 1px solid var(--ap-alert-wash);
	border-radius: var(--ap-radius-pill);
	padding: 3px 12px;
	margin: 0;
	background-color: var(--ap-alert-wash);
	color: var(--ap-alert);
	font-family: var(--ap-font);
	font-size: 12px;
	font-weight: 600;
	filter: none;
	transition: background-color 0.14s var(--ap-ease), transform 0.14s var(--ap-ease);

	&:hover {
		padding: 3px 12px;
		transform: translateY(-1px);
		border-color: var(--ap-alert);
		background-color: var(--ap-alert-wash);
		filter: none;
	}

	&:active {
		transform: translateY(0);
		background-color: var(--ap-alert-wash);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-alert);
		outline-offset: 1px;
	}
}

// Pie -----------------------------------------------------------------------
.rf-foot {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 16px 28px;
	background-color: var(--ap-paper);
	border-top: 1px solid var(--ap-line);
}

.rf-foot__note {
	font-size: 12px;
	color: var(--ap-muted);
}

.rf-foot__spacer {
	flex: 1;
}

.button {
	border-radius: var(--ap-radius-pill);
	border: 1px solid var(--ap-line-strong);
	font-family: var(--ap-font);
	font-weight: 600;
	color: var(--ap-slate);
	transition: transform 0.14s var(--ap-ease), background-color 0.14s var(--ap-ease), border-color 0.14s var(--ap-ease);

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 1px;
	}
}

.button.is-primary {
	border-color: transparent;
	background-color: var(--ap-ink);
	color: var(--ap-paper);

	&:hover {
		transform: translateY(-1px);
		background-color: var(--ap-teal-deep);
	}

	&:active {
		transform: translateY(0);
	}

	&:disabled {
		background-color: var(--ap-mist);
		color: var(--ap-muted);
		transform: none;
		cursor: not-allowed;
	}
}

@media (prefers-reduced-motion: reduce) {
	.rf-close,
	.fecha_input,
	.log_row:not(.log_header),
	.error_cell :deep(.simple_button),
	.button {
		transition: none;
	}

	.rf-live__dot {
		animation: none;
	}
}
</style>
